import { ITrackingDiagnostic } from "types/models/TrackingDiagnosticsModel";
import { BaseStoreClass, IBaseStoreClass } from "./BaseStoreClass";
import { INotificationsStore } from "./NotificationsStore";
import { action, makeObservable, observable } from "mobx";
import { getErrorMessage } from "utilities/ErrorHelper";
import { assignTrackingDiagnosticsSessionTypeColors, assignTrackingDiagnosticsTypeColors } from 'utilities/SystemUtils';
import { ITrackingDiagnosticsService } from "services/TrackingDiagnosticsService";

export interface ITrackingDiagnosticsStore extends IBaseStoreClass {
  trackingDiagnostics: ITrackingDiagnostic[];
  trackingDiagnosticsTypes: string[];
  sessionTypes: string[];
  currentSelectedTrackingDiagnostic: ITrackingDiagnostic | null;
  allSelectedTrackingDiagnosticIds: string[] | null;

  isLoadingTrackingDiagnostics: boolean;

  isBusy: boolean;

  setCurrentSelectedTrackingDiagnostic(trackingDiagnostic: ITrackingDiagnostic | null): void;
  getTrackingDiagnostics(): void;
}

export class TrackingDiagnosticsStore extends BaseStoreClass implements ITrackingDiagnosticsStore {
  trackingDiagnostics: ITrackingDiagnostic[] = [];
  trackingDiagnosticsTypes: string[] = [];
  sessionTypes: string[] = [];
  currentSelectedTrackingDiagnostic: ITrackingDiagnostic | null = null;
  allSelectedTrackingDiagnosticIds: string[] | null = [];

  isLoadingTrackingDiagnostics: boolean = false;

  isBusy: boolean = this.isLoadingTrackingDiagnostics;

  _notificationsStore: INotificationsStore;
  _trackingDiagnosticsService: ITrackingDiagnosticsService;

  constructor(
    trackingDiagnosticsService: ITrackingDiagnosticsService,
    notificationsStore: INotificationsStore) {
    super();

    this.take = 100;

    this._notificationsStore = notificationsStore;
    this._trackingDiagnosticsService = trackingDiagnosticsService;

    makeObservable(this, {
      trackingDiagnostics: observable,
      trackingDiagnosticsTypes: observable,
      sessionTypes: observable,
      allSelectedTrackingDiagnosticIds: observable,
      isLoadingTrackingDiagnostics: observable,
      isBusy: observable,
      currentSelectedTrackingDiagnostic: observable,
      setCurrentSelectedTrackingDiagnostic: action,
      getTrackingDiagnostics: action,
    });
  }

  async getTrackingDiagnostics() {
    try {
      this.setIsLoadingTrackingDiagnostics(true);

      const trackingDiagnosticsResponse = await this._trackingDiagnosticsService.getAllTrackingDiagnostics({
        page: this.page - 1, //Backend Pagination starts from 0
        pageSize: this.take,
        updatedAfter: "2025-10-01",
        includeDeleted: false
      });

      let trackingDiagnosticsDtoData: ITrackingDiagnostic[] = trackingDiagnosticsResponse.data;
      if (!trackingDiagnosticsDtoData || trackingDiagnosticsResponse.isError) {
        return;
      }

      let destictTypes = Array.from(new Set(trackingDiagnosticsDtoData.map(item => item.type)));
      let destictSessionTypes = Array.from(new Set(trackingDiagnosticsDtoData.map(item => item.sessionType)));

      this.setTrackingDiagnostics(trackingDiagnosticsDtoData);
      this.setTrackingDiagnosticsTypes(destictTypes);
      this.setTrackingDiagnosticsSessionTypes(destictSessionTypes);
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsLoadingTrackingDiagnostics(false);
    }
  }

  // =========================== UTILS ===========================

  updateIsBusy() {
    this.isBusy = this.isLoadingTrackingDiagnostics;
  }

  setIsLoadingTrackingDiagnostics(status: boolean) {
    this.isLoadingTrackingDiagnostics = status;
    this.updateIsBusy()
  }

  setTrackingDiagnostics(trackingDiagnostics: ITrackingDiagnostic[]) {
    this.trackingDiagnostics = trackingDiagnostics;
  }

  setTrackingDiagnosticsTypes(types: string[]) {
    this.trackingDiagnosticsTypes = types;
    assignTrackingDiagnosticsTypeColors(types);
  }

  setTrackingDiagnosticsSessionTypes(sessionTypes: string[]) {
    this.sessionTypes = sessionTypes;
    assignTrackingDiagnosticsSessionTypeColors(sessionTypes);
  }

  setCurrentSelectedTrackingDiagnostic = (trackingDiagnostic: ITrackingDiagnostic | null) => {
    this.currentSelectedTrackingDiagnostic = trackingDiagnostic;
  };

  addSelectedTrackingDiagnosticList = (trackingDiagnostic: ITrackingDiagnostic) => {
    if (!trackingDiagnostic || trackingDiagnostic.hasFile === false) return;
    const current = this.allSelectedTrackingDiagnosticIds ?? [];
    const exists = current.includes(trackingDiagnostic.id);
    if (!exists) {
      this.allSelectedTrackingDiagnosticIds = [...current, trackingDiagnostic.id];
    }
  };

  removeSelectedTrackingDiagnosticList = (trackingDiagnostic: ITrackingDiagnostic) => {
    const current = this.allSelectedTrackingDiagnosticIds ?? [];
    const filtered = current.filter(id => id !== trackingDiagnostic.id);
    this.allSelectedTrackingDiagnosticIds = filtered;
  };

  addRangeSelectedTrackingDiagnosticList = (trackingDiagnostics: ITrackingDiagnostic[]) => {
    if (!trackingDiagnostics || trackingDiagnostics.length === 0) return;
    const filteredTrackingDiagnostics = trackingDiagnostics.filter(td => td.hasFile);
    const current = this.allSelectedTrackingDiagnosticIds ?? [];
    const ids = new Set(current);
    const toAdd = filteredTrackingDiagnostics.map(td => td.id).filter(id => !ids.has(id));
    if (toAdd.length > 0) {
      this.allSelectedTrackingDiagnosticIds = [...current, ...toAdd];
    }
  };

  removeRangeSelectedTrackingDiagnosticList = (trackingDiagnostics: ITrackingDiagnostic[]) => {
    if (!trackingDiagnostics || trackingDiagnostics.length === 0) return;
    const idsToRemove = new Set(trackingDiagnostics.map(td => td.id));
    const current = this.allSelectedTrackingDiagnosticIds ?? [];
    const filtered = current.filter(id => !idsToRemove.has(id));
    this.allSelectedTrackingDiagnosticIds = filtered;
  };

  clearSelectedTrackingDiagnosticList = () => {
    if (!this.allSelectedTrackingDiagnosticIds || this.allSelectedTrackingDiagnosticIds.length === 0) return;
    this.allSelectedTrackingDiagnosticIds = [];
  };
}